import { KnownMembership, MatrixEvent, RoomMember, RoomMemberEvent } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useBotValidatedAction, { BotValidatedState } from 'hooks/useBotValidatedAction/useBotValidatedAction';
import { MEEDIO_BOT_ID } from 'utils/matrixUtils';

// Decision 25 (OD-03 resolved): bot join must complete within 30 000 ms. The
// constant lives at the call site so the wait is visible to consumers and the
// 30 s ceiling is enforced by `useBotValidatedAction`'s timeout machinery.
export const BOT_JOIN_TIMEOUT_MS = 30_000;

export interface UseBotJoinGateReturn {
  state: BotValidatedState;
  waitForBotJoin: (roomId: string) => Promise<void>;
  reset: () => void;
}

// Gap 10 — Bot-join hard gate on workspace creation. Wraps the canonical
// `useBotValidatedAction` primitive (Decision 24) with a single-purpose
// listener: resolves on the Meedio bot's `membership === 'join'` for the given
// room. Fast-path: bot already joined at call time → resolves immediately.
// `timeoutIsSuccess: false` so a 30 s timeout surfaces as `'timeout'` rather
// than spurious success (Gap 10 explicitly forbids advancing the modal when
// the bot hasn't acknowledged the room).
const useBotJoinGate = (): UseBotJoinGateReturn => {
  const { matrixClient } = useMatrixContext();
  const roomIdRef = useRef<string | null>(null);
  const membershipHandlerRef = useRef<((event: MatrixEvent, member: RoomMember) => void) | null>(null);

  const unsubscribe = useCallback(() => {
    if (membershipHandlerRef.current) {
      matrixClient.off(RoomMemberEvent.Membership, membershipHandlerRef.current);
      membershipHandlerRef.current = null;
    }
  }, [matrixClient]);

  const action = useCallback(async (): Promise<void> => {
    const roomId = roomIdRef.current;
    if (!roomId || !MEEDIO_BOT_ID) throw new Error('useBotJoinGate: missing roomId or MEEDIO_BOT_ID');

    const room = matrixClient.getRoom(roomId);
    if (room?.getMember(MEEDIO_BOT_ID)?.membership === KnownMembership.Join) return;

    return new Promise<void>((resolve) => {
      const handler = (_event: MatrixEvent, member: RoomMember) => {
        if (member.roomId !== roomId) return;
        if (member.userId !== MEEDIO_BOT_ID) return;
        if (member.membership === KnownMembership.Join) resolve();
      };
      membershipHandlerRef.current = handler;
      matrixClient.on(RoomMemberEvent.Membership, handler);
    });
  }, [matrixClient]);

  const { state, execute, reset } = useBotValidatedAction<void>({
    action,
    onSuccess: unsubscribe,
    onReject: unsubscribe,
    timeoutMs: BOT_JOIN_TIMEOUT_MS,
    timeoutIsSuccess: false,
  });

  useEffect(() => {
    if (state !== 'pending') unsubscribe();
  }, [state, unsubscribe]);

  useEffect(() => () => unsubscribe(), [unsubscribe]);

  const waitForBotJoin = useCallback(
    async (roomId: string) => {
      roomIdRef.current = roomId;
      await execute();
    },
    [execute]
  );

  return { state, waitForBotJoin, reset };
};

export default useBotJoinGate;
